import { useState, useEffect, useMemo } from 'react';
import { Stack, Typography } from '@mui/material';
import {
  CurrentItemIcon,
  EquipmentName,
  NavigationContainer,
  SectionItem,
  SectionTitle,
} from '../styles';
import { abyssColors } from '@/theme/colors';
import { useEquipmentList } from '../../hooks';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { CustomSkeleton } from './styles';
import { useAssemblyData } from '../../../ReviewScreen/hooks/useAssemblyData';
import { generatePoiDisplayName } from '../../../modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/utils';
import { useGetGenericDetailsPanels } from '../../../ReviewScreen/hooks/useGetGenericDetailsPanels';

const scrollToElement = (id: string) => {
  const element = document.querySelector(`#${CSS.escape(id)}`);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

const ListSkeleton = () => {
  return (
    <Stack gap={2}>
      <CustomSkeleton
        animation="wave"
        variant="text"
        width={90}
        height={24}
        sx={{ marginLeft: 4 }}
      />
      <Stack marginBottom="4px" marginLeft={5} gap={1.5}>
        <CustomSkeleton animation="wave" variant="text" width={90} height={16} />
        <CustomSkeleton animation="wave" variant="text" width={60} height={16} />
        <CustomSkeleton animation="wave" variant="text" width={70} height={16} />
        <CustomSkeleton animation="wave" variant="text" width={50} height={16} />
      </Stack>
    </Stack>
  );
};

const NavigatorSkeleton = () => {
  return (
    <Stack gap={6} marginBottom={6}>
      <Stack direction="row" alignItems="center" paddingX={3}>
        <CustomSkeleton animation="wave" variant="text" width={200} height={30} />
      </Stack>
      {Array.from({ length: 2 }).map((_, index) => (
        <ListSkeleton key={index} />
      ))}
    </Stack>
  );
};

export const ComponentReportNavigator = () => {
  const [selectedItem, setSelectedItem] = useState<string | undefined>(undefined);
  const selectedReport = useAtomValue(state.selectedReport);
  const equipmentList = useEquipmentList(selectedReport);
  const { assemblyDetails } = useAssemblyData({
    assemblyId: selectedReport?.componentIds?.at(0) || '',
  });
  const { detailsData } = useGetGenericDetailsPanels(assemblyDetails);
  const navbarHeight = useNavbarHeight();

  useEffect(() => {
    if (Object.keys(equipmentList).length > 0 && !selectedItem) {
      setSelectedItem(Object.keys(equipmentList)[0]);
    }
  }, [equipmentList, selectedItem]);

  const handleClick = (item: string) => {
    setSelectedItem(item);
    scrollToElement(item);
  };

  const isLoading = useMemo(() => {
    return !detailsData || !selectedReport;
  }, [detailsData, selectedReport]);

  const specifications = useMemo(() => {
    return [
      ...(detailsData || []),
      { title: 'CVI Checklist', data: [] },
      {
        title: 'GVI Checklist',
        data: [],
      },
      {
        title: 'Attachments',
        data: [],
      },
    ];
  }, [detailsData]);

  const hasData = useMemo(() => {
    return !!selectedReport?.pointOfInterests?.length || !!selectedReport?.componentIds?.length;
  }, [selectedReport]);

  return (
    <NavigationContainer navbarHeight={navbarHeight} data-testId="report-navigation">
      <Typography
        color={abyssColors.primary[700]}
        fontWeight={700}
        m={3}
        data-testid="report-navigator-title"
      >
        Contents
      </Typography>
      {isLoading && hasData && <NavigatorSkeleton />}
      {!isLoading && hasData && (
        <Stack gap={2} marginBottom={6}>
          <Stack direction="row" alignItems="center" paddingX={3}>
            <EquipmentName>{equipmentList.at(0)?.tagName}</EquipmentName>
          </Stack>
          <Stack data-testid="report-navigator-equipment">
            <SectionTitle selected>Specifications</SectionTitle>
            {specifications?.map((detail) => (
              <Stack
                key={detail.title}
                direction="row"
                alignItems="center"
                marginBottom="4px"
                data-testid="report-navigator-detail"
              >
                <CurrentItemIcon selected={selectedItem === detail.title} />
                <SectionItem
                  selected={selectedItem === detail.title}
                  onClick={() => {
                    handleClick(detail.title);
                  }}
                >
                  {detail.title}
                </SectionItem>
              </Stack>
            ))}
            <SectionTitle selected>Point of interests</SectionTitle>
            {selectedReport?.pointOfInterests?.map((point) => (
              <Stack
                key={point.id}
                direction="row"
                alignItems="center"
                marginBottom="4px"
                data-testid="report-navigator-poi"
              >
                <CurrentItemIcon selected={selectedItem === point.id} />
                <SectionItem
                  selected={selectedItem === point.id}
                  onClick={() => {
                    handleClick(point.id);
                  }}
                >
                  {generatePoiDisplayName({
                    name: point.name,
                    poiType: point.type,
                    poiId: point.poiId,
                  })}
                </SectionItem>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </NavigationContainer>
  );
};
